import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { usePaymentPlans, useWallets, QUERY_KEYS } from "@/hooks/use-queries";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useState, useRef, useEffect } from "react";
import { useAnimatedCounter } from "@/hooks/use-animated-counter";
import { useConfetti } from "@/components/confetti-burst";
import { 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Clock, 
  PauseCircle, 
  PlayCircle, 
  AlertCircle, 
  CheckCircle,
  Building2,
  Plus,
  Calculator,
  FileText,
  TrendingUp,
  Wallet,
  ArrowRight,
  Search,
  MapPin,
  Loader2
} from "lucide-react";
import { format, differenceInDays, addMonths } from "date-fns";
import { PageAbout } from "@/components/page-about";

interface PaymentPlan {
  id: string;
  walletId: string;
  userId: string;
  hospitalName: string;
  serviceDescription: string | null;
  originalBalance: number;
  remainingBalance: number;
  totalPaid: number;
  paymentAmount: number;
  frequency: "weekly" | "biweekly" | "monthly";
  paymentDayOfWeek: number | null;
  paymentDayOfMonth: number | null;
  durationMonths: number;
  nextPaymentDate: string | null;
  status: "active" | "paused" | "completed" | "defaulted" | "cancelled";
  startDate: string;
  endDate: string | null;
  completedAt: string | null;
  isPaused: boolean;
  pausedAt: string | null;
  pauseReason: string | null;
  pauseEndDate: string | null;
  totalPauseDays: number;
  autoPayEnabled: boolean;
  paymentMethod: string | null;
  createdAt: string;
  payments?: PaymentPlanPayment[];
}

interface PaymentPlanPayment {
  id: string;
  planId: string;
  amount: number;
  scheduledDate: string;
  paidAt: string | null;
  status: "scheduled" | "completed" | "failed" | "skipped";
  paymentMethod: string | null;
  walletTransactionId: string | null;
  failureReason: string | null;
  notes: string | null;
}

interface PaymentSuggestion {
  durationMonths: number;
  paymentAmount: number;
  frequency: string;
}

interface Wallet {
  id: string;
  userId: string;
  patientName: string;
  balance: number;
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getStatusColor(status: string): string {
  switch (status) {
    case "active": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "paused": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "completed": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "defaulted": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    case "cancelled": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    default: return "bg-gray-100 text-gray-800";
  }
}

function getPaymentStatusIcon(status: string) {
  switch (status) {
    case "completed": return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "scheduled": return <Clock className="h-4 w-4 text-blue-500" />;
    case "failed": return <AlertCircle className="h-4 w-4 text-red-500" />;
    case "skipped": return <PauseCircle className="h-4 w-4 text-yellow-500" />;
    default: return <Clock className="h-4 w-4" />;
  }
}

export default function PaymentPlansPage() {
  usePageTitle("Payment Schedules", "View and manage hospital-facilitated payment schedules for medical expenses on HealthWallet.");
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  
  const { data: plans, isLoading: plansLoading } = usePaymentPlans();

  const { data: wallets, isLoading: walletsLoading } = useWallets();

  const activePlans = plans?.filter(p => p.status === "active" || p.status === "paused") || [];
  const completedPlans = plans?.filter(p => p.status === "completed") || [];
  const totalRemaining = activePlans.reduce((sum, p) => sum + p.remainingBalance, 0);
  const totalPaid = plans?.reduce((sum, p) => sum + p.totalPaid, 0) || 0;
  const nextPaymentDate = activePlans
    .filter(p => p.nextPaymentDate && !p.isPaused)
    .sort((a, b) => new Date(a.nextPaymentDate!).getTime() - new Date(b.nextPaymentDate!).getTime())[0]?.nextPaymentDate;

  const animatedRemaining = useAnimatedCounter(totalRemaining / 100, { duration: 900 });
  const animatedTotalPaid = useAnimatedCounter(totalPaid / 100, { duration: 900, delay: 100 });
  const { fire: fireConfetti, ConfettiCanvas } = useConfetti();

  const prevCompletedCountRef = useRef(completedPlans.length);
  useEffect(() => {
    if (completedPlans.length > prevCompletedCountRef.current && prevCompletedCountRef.current >= 0) {
      if (prevCompletedCountRef.current > 0) {
        fireConfetti();
      }
    }
    prevCompletedCountRef.current = completedPlans.length;
  }, [completedPlans.length, fireConfetti]);

  if (plansLoading) {
    return (
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-36" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tab bar */}
        <div className="flex gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>

        {/* Plan card skeletons */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-5 rounded-full" />
                      <Skeleton className="h-5 w-40" />
                    </div>
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-12" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                  <Skeleton className="h-2 w-full rounded-full" />
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-px w-full" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2">
                <Skeleton className="h-8 w-28 rounded-md" />
                <Skeleton className="h-8 w-20 rounded-md" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfettiCanvas />
      <PageAbout
        title="Post-Care Payment Schedules"
        description="Manage medical expenses with flexible payment schedules arranged through your healthcare provider. Break down large bills into affordable monthly payments."
        features={[
          "Pay only the original amount you owe - no additional fees",
          "Flexible payment terms from 3 to 60 months",
          "Choose weekly, bi-weekly, or monthly payments",
          "AI-powered suggestions for optimal payment amounts",
          "Hardship support: pause, reduce, or extend plans when needed"
        ]}
        tips={[
          "Use the AI suggestions to find a payment amount that fits your budget",
          "Set up auto-pay to never miss a payment",
          "If facing financial hardship, pause your schedule rather than missing payments"
        ]}
      />
      
      <div className="p-4 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1" data-testid="text-payment-not-insurance">
        <p>Payment schedules are facilitated by participating healthcare providers (hospital partners). HealthWallet provides the technology to track and manage these arrangements. HealthWallet does not extend credit, charge interest, or act as a lender. This is not an insurance product, health benefit plan, or loan. All funds are processed by Stripe, Inc.</p>
        <p>See our <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.</p>
      </div>
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="text-payment-plans-title">
            Post-Care Payment Schedules
          </h1>
          <p className="text-muted-foreground">
            Manage payment schedules for medical expenses
          </p>
        </div>
        {(user?.role === "dispatcher" || user?.role === "admin") && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="button-create-plan">
            <Plus className="mr-2 h-4 w-4" />
            Create Payment Schedule
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Remaining</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-remaining">
              ${animatedRemaining.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Across {activePlans.length} active plan{activePlans.length !== 1 ? "s" : ""}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-total-paid">
              ${animatedTotalPaid.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              {completedPlans.length} plan{completedPlans.length !== 1 ? "s" : ""} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Payment</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-next-payment">
              {nextPaymentDate ? format(new Date(nextPaymentDate), "MMM d, yyyy") : "No upcoming"}
            </div>
            <p className="text-xs text-muted-foreground">
              {nextPaymentDate 
                ? (() => {
                    const daysUntil = differenceInDays(new Date(nextPaymentDate), new Date());
                    if (daysUntil < 0) return `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? "s" : ""}`;
                    if (daysUntil === 0) return "Due today";
                    return `In ${daysUntil} day${daysUntil !== 1 ? "s" : ""}`;
                  })()
                : "All plans completed or paused"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active-plans">
            Active ({activePlans.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed-plans">
            Completed ({completedPlans.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activePlans.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Active Payment Schedules</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Set up a payment schedule to start managing medical expenses.
                </p>
                {(user?.role === "dispatcher" || user?.role === "admin") && (
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Plan
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {activePlans.map((plan) => (
                <PaymentPlanCard 
                  key={plan.id} 
                  plan={plan}
                  onViewDetails={() => setSelectedPlan(plan)}
                  onMakePayment={() => {
                    setSelectedPlan(plan);
                    setShowPaymentDialog(true);
                  }}
                  onPause={() => {
                    setSelectedPlan(plan);
                    setShowPauseDialog(true);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedPlans.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Completed Plans Yet</h3>
                <p className="text-muted-foreground text-center">
                  Completed payment schedules will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {completedPlans.map((plan) => (
                <PaymentPlanCard 
                  key={plan.id} 
                  plan={plan}
                  onViewDetails={() => setSelectedPlan(plan)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {showCreateDialog && (
        <CreatePlanDialog 
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          wallets={wallets || []}
          userId={user?.id || ""}
        />
      )}

      {showPaymentDialog && selectedPlan && (
        <MakePaymentDialog
          open={showPaymentDialog}
          onOpenChange={setShowPaymentDialog}
          plan={selectedPlan}
        />
      )}

      {showPauseDialog && selectedPlan && (
        <PausePlanDialog
          open={showPauseDialog}
          onOpenChange={setShowPauseDialog}
          plan={selectedPlan}
        />
      )}

      {selectedPlan && !showPaymentDialog && !showPauseDialog && (
        <PlanDetailsDialog
          open={!!selectedPlan}
          onOpenChange={(open) => !open && setSelectedPlan(null)}
          plan={selectedPlan}
        />
      )}
    </div>
  );
}

function PaymentPlanCard({ 
  plan, 
  onViewDetails, 
  onMakePayment, 
  onPause 
}: { 
  plan: PaymentPlan; 
  onViewDetails: () => void; 
  onMakePayment?: () => void;
  onPause?: () => void;
}) {
  const progress = ((plan.originalBalance - plan.remainingBalance) / plan.originalBalance) * 100;
  const isCompleted = plan.status === "completed";
  
  const isOverdue = plan.status === "active" && !plan.isPaused && plan.nextPaymentDate 
    ? new Date(plan.nextPaymentDate) < new Date(new Date().toDateString())
    : false;

  return (
    <Card className={`hover-elevate cursor-pointer ${isCompleted ? "animate-pulse-glow" : ""}`} onClick={onViewDetails} data-testid={`card-plan-${plan.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              {plan.hospitalName}
            </CardTitle>
            {plan.serviceDescription && (
              <CardDescription className="mt-1">{plan.serviceDescription}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {isOverdue && (
              <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" data-testid={`badge-overdue-${plan.id}`}>
                Overdue
              </Badge>
            )}
            <Badge className={getStatusColor(plan.status)}>
              {plan.isPaused ? "Paused" : plan.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span>Progress</span>
            <span className="font-medium">{progress.toFixed(0)}%</span>
          </div>
          <div className={progress >= 100 ? "animate-milestone-shine rounded-full" : ""}>
            <Progress value={progress} className="h-2" />
          </div>
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{formatCurrency(plan.totalPaid)} paid</span>
            <span>{formatCurrency(plan.remainingBalance)} remaining</span>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Payment Amount</p>
            <p className="font-semibold">{formatCurrency(plan.paymentAmount)} / {plan.frequency}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Next Payment</p>
            <p className="font-semibold">
              {plan.isPaused 
                ? `Paused until ${plan.pauseEndDate ? format(new Date(plan.pauseEndDate), "MMM d") : "resumed"}`
                : plan.nextPaymentDate 
                  ? format(new Date(plan.nextPaymentDate), "MMM d, yyyy")
                  : "N/A"
              }
            </p>
          </div>
        </div>
      </CardContent>
      {(plan.status === "active" || plan.status === "paused") && (
        <CardFooter className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          {onMakePayment && !plan.isPaused && (
            <Button size="sm" onClick={onMakePayment} data-testid={`button-pay-${plan.id}`}>
              <DollarSign className="mr-1 h-4 w-4" />
              Make Payment
            </Button>
          )}
          {onPause && !plan.isPaused && (
            <Button variant="outline" size="sm" onClick={onPause} data-testid={`button-pause-${plan.id}`}>
              <PauseCircle className="mr-1 h-4 w-4" />
              Pause
            </Button>
          )}
          {plan.isPaused && (
            <ResumePlanButton planId={plan.id} />
          )}
        </CardFooter>
      )}
    </Card>
  );
}

function ResumePlanButton({ planId }: { planId: string }) {
  const { toast } = useToast();
  const resumeMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/payment-plans/${planId}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentPlans });
      toast({ title: "Schedule Resumed", description: "Your payment schedule has been resumed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={() => resumeMutation.mutate()}
      disabled={resumeMutation.isPending}
      data-testid={`button-resume-${planId}`}
    >
      <PlayCircle className="mr-1 h-4 w-4" />
      Resume
    </Button>
  );
}

interface ProviderOption {
  id: string;
  name: string;
  providerType: string;
  city?: string | null;
  state?: string | null;
  address?: string | null;
}

function ProviderSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string) => void;
}) {
  const [searchText, setSearchText] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value && !searchText) setSearchText(value);
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.length >= 2 ? searchText : "");
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchUrl = `/api/healthcare-providers?search=${encodeURIComponent(debouncedSearch)}`;
  const { data: providers, isLoading } = useQuery<ProviderOption[]>({
    queryKey: [searchUrl],
    enabled: debouncedSearch.length >= 2,
  });

  const handleSelect = (provider: ProviderOption) => {
    const displayName = provider.city
      ? `${provider.name} — ${provider.city}, ${provider.state || "MN"}`
      : provider.name;
    setSearchText(displayName);
    onChange(displayName);
    setShowDropdown(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    onChange(val);
    setShowDropdown(val.length >= 2);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          id="hospital"
          placeholder="Search providers, e.g. Mayo Clinic..."
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => { if (searchText.length >= 2) setShowDropdown(true); }}
          className="pl-9"
          data-testid="input-hospital"
          autoComplete="off"
        />
        {isLoading && debouncedSearch && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {showDropdown && debouncedSearch && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-60 overflow-y-auto" data-testid="provider-dropdown">
          {isLoading ? (
            <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching providers...
            </div>
          ) : providers && providers.length > 0 ? (
            providers.slice(0, 15).map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start rounded-none font-normal no-default-hover-elevate no-default-active-elevate"
                onClick={() => handleSelect(provider)}
                data-testid={`provider-option-${provider.id}`}
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate" data-testid={`provider-name-${provider.id}`}>{provider.name}</span>
                {provider.city && (
                  <span className="text-xs text-muted-foreground ml-auto shrink-0 flex items-center gap-0.5" data-testid={`provider-location-${provider.id}`}>
                    <MapPin className="h-3 w-3" />
                    {provider.city}{provider.state ? `, ${provider.state}` : ""}
                  </span>
                )}
              </Button>
            ))
          ) : (
            <div className="p-3 text-sm text-muted-foreground">
              No providers found. You can still type a name manually.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePlanDialog({ 
  open, 
  onOpenChange, 
  wallets,
  userId 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  wallets: Wallet[];
  userId: string;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    walletId: "",
    hospitalName: "",
    serviceDescription: "",
    originalBalance: "",
    frequency: "monthly",
    paymentDayOfMonth: "15",
    paymentDayOfWeek: "5",
    selectedSuggestion: null as PaymentSuggestion | null,
  });

  const balanceInCents = Math.round(parseFloat(formData.originalBalance || "0") * 100);
  
  const { data: suggestions, isLoading: suggestionsLoading } = useQuery<PaymentSuggestion[]>({
    queryKey: ["/api/payment-suggestions", balanceInCents],
    queryFn: async () => {
      const res = await fetch(`/api/payment-suggestions?balance=${balanceInCents}`, { credentials: 'include' });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: balanceInCents > 0,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/payment-plans", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentPlans });
      toast({ title: "Schedule Created", description: "Your payment schedule has been created successfully." });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (!formData.selectedSuggestion) return;
    
    const selectedFrequency = formData.selectedSuggestion.frequency;
    const plan = {
      walletId: formData.walletId,
      userId,
      hospitalName: formData.hospitalName,
      serviceDescription: formData.serviceDescription || undefined,
      originalBalance: balanceInCents,
      paymentAmount: formData.selectedSuggestion.paymentAmount,
      frequency: selectedFrequency,
      durationMonths: formData.selectedSuggestion.durationMonths,
      paymentDayOfMonth: selectedFrequency === "monthly" ? parseInt(formData.paymentDayOfMonth) : undefined,
      paymentDayOfWeek: selectedFrequency !== "monthly" ? parseInt(formData.paymentDayOfWeek) : undefined,
      autoPayEnabled: true,
    };
    
    createMutation.mutate(plan);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Payment Schedule</DialogTitle>
          <DialogDescription>
            Set up a payment schedule for medical expenses
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="wallet">Patient Wallet</Label>
              <Select value={formData.walletId} onValueChange={(v) => setFormData({ ...formData, walletId: v })}>
                <SelectTrigger data-testid="select-wallet">
                  <SelectValue placeholder="Select a wallet" />
                </SelectTrigger>
                <SelectContent>
                  {wallets.map((wallet) => (
                    <SelectItem key={wallet.id} value={wallet.id}>
                      {wallet.patientName} ({formatCurrency(wallet.balance)} balance)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hospital">Hospital / Provider Name</Label>
              <ProviderSearchInput
                value={formData.hospitalName}
                onChange={(name) => setFormData({ ...formData, hospitalName: name })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service">Service Description (Optional)</Label>
              <Input
                id="service"
                placeholder="e.g., Emergency room visit, Surgery"
                value={formData.serviceDescription}
                onChange={(e) => setFormData({ ...formData, serviceDescription: e.target.value })}
                data-testid="input-service"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="balance">Total Balance ($)</Label>
              <Input
                id="balance"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 2500.00"
                value={formData.originalBalance}
                onChange={(e) => setFormData({ ...formData, originalBalance: e.target.value })}
                data-testid="input-balance"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button 
                onClick={() => setStep(2)}
                disabled={!formData.walletId || !formData.hospitalName || !formData.originalBalance}
                data-testid="button-next-step"
              >
                Next: Choose Payment Schedule
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between gap-2 p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">{formData.hospitalName}</p>
                <p className="text-sm text-muted-foreground">{formData.serviceDescription || "Medical Services"}</p>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(balanceInCents)}</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                AI-Suggested Payment Options
              </Label>
              <p className="text-xs text-muted-foreground">
                Based on the balance, we recommend these payment schedules:
              </p>
            </div>

            {suggestionsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : (
              <div className="space-y-2">
                {suggestions?.map((suggestion, idx) => (
                  <Card 
                    key={idx}
                    className={`cursor-pointer transition-all ${
                      formData.selectedSuggestion === suggestion 
                        ? "ring-2 ring-primary" 
                        : "hover-elevate"
                    }`}
                    onClick={() => setFormData({ ...formData, selectedSuggestion: suggestion })}
                    data-testid={`suggestion-${idx}`}
                  >
                    <CardContent className="flex items-center justify-between gap-2 p-4">
                      <div>
                        <p className="font-medium">{formatCurrency(suggestion.paymentAmount)}/{suggestion.frequency}</p>
                        <p className="text-sm text-muted-foreground">
                          {suggestion.durationMonths} months • Ends {format(addMonths(new Date(), suggestion.durationMonths), "MMM yyyy")}
                        </p>
                      </div>
                      {formData.selectedSuggestion === suggestion && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {formData.selectedSuggestion && formData.selectedSuggestion.frequency !== "monthly" && (
              <div className="space-y-2">
                <Label htmlFor="payDayOfWeek">Payment Day of Week</Label>
                <Select 
                  value={formData.paymentDayOfWeek} 
                  onValueChange={(v) => setFormData({ ...formData, paymentDayOfWeek: v })}
                >
                  <SelectTrigger data-testid="select-pay-day-week">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { value: "1", label: "Monday" },
                      { value: "2", label: "Tuesday" },
                      { value: "3", label: "Wednesday" },
                      { value: "4", label: "Thursday" },
                      { value: "5", label: "Friday" },
                    ].map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.selectedSuggestion && formData.selectedSuggestion.frequency === "monthly" && (
              <div className="space-y-2">
                <Label htmlFor="payDay">Payment Day of Month</Label>
                <Select 
                  value={formData.paymentDayOfMonth} 
                  onValueChange={(v) => setFormData({ ...formData, paymentDayOfMonth: v })}
                >
                  <SelectTrigger data-testid="select-pay-day">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 5, 10, 15, 20, 25].map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day === 1 ? "1st" : day === 15 ? "15th (mid-month)" : `${day}th`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button 
                onClick={handleCreate}
                disabled={!formData.selectedSuggestion || createMutation.isPending}
                data-testid="button-create-plan-submit"
              >
                {createMutation.isPending ? "Creating..." : "Create Payment Schedule"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MakePaymentDialog({ 
  open, 
  onOpenChange, 
  plan 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  plan: PaymentPlan;
}) {
  const { toast } = useToast();
  const [amount, setAmount] = useState((plan.paymentAmount / 100).toFixed(2));
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "card">("wallet");

  const { data: wallet } = useQuery<Wallet>({
    queryKey: ["/api/wallets", plan.walletId],
  });

  const paymentMutation = useMutation({
    mutationFn: (data: { amount: number; paymentMethod: string }) => 
      apiRequest("POST", `/api/payment-plans/${plan.id}/payments`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentPlans });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wallets });
      toast({ title: "Payment Successful", description: "Your payment has been processed." });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Payment Failed", description: error.message, variant: "destructive" });
    },
  });

  const amountCents = Math.round(parseFloat(amount || "0") * 100);
  const canPayFromWallet = wallet && wallet.balance >= amountCents && amountCents > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Make Payment</DialogTitle>
          <DialogDescription>
            Pay towards your {plan.hospitalName} balance
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between gap-2">
              <span>Remaining Balance</span>
              <span className="font-bold">{formatCurrency(plan.remainingBalance)}</span>
            </div>
            <div className="flex justify-between gap-2 text-sm text-muted-foreground">
              <span>Regular Payment</span>
              <span>{formatCurrency(plan.paymentAmount)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Payment Amount ($)</Label>
            <Input
              id="amount"
              type="number"
              min="0"
              max={(plan.remainingBalance / 100).toFixed(2)}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value || "0")}
              data-testid="input-payment-amount"
            />
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAmount((plan.paymentAmount / 100).toFixed(2))}
              >
                Regular ({formatCurrency(plan.paymentAmount)})
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAmount((plan.remainingBalance / 100).toFixed(2))}
              >
                Pay in Full
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <div className="grid grid-cols-2 gap-2">
              <Card 
                className={`cursor-pointer p-3 ${paymentMethod === "wallet" ? "ring-2 ring-primary" : "hover-elevate"}`}
                onClick={() => setPaymentMethod("wallet")}
              >
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  <span>HealthWallet</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Balance: {wallet ? formatCurrency(wallet.balance) : "Loading..."}
                </p>
              </Card>
              <Card 
                className={`cursor-pointer p-3 ${paymentMethod === "card" ? "ring-2 ring-primary" : "hover-elevate"}`}
                onClick={() => setPaymentMethod("card")}
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  <span>Card</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Debit or credit card
                </p>
              </Card>
            </div>
          </div>

          {paymentMethod === "wallet" && amountCents > 0 && wallet && wallet.balance < amountCents && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              Insufficient funds. Add funds to your wallet or use a card.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={() => paymentMutation.mutate({ amount: amountCents, paymentMethod })}
            disabled={
              amountCents <= 0 || 
              amountCents > plan.remainingBalance || 
              (paymentMethod === "wallet" && !canPayFromWallet) ||
              paymentMutation.isPending
            }
            data-testid="button-confirm-payment"
          >
            {paymentMutation.isPending ? "Processing..." : `Pay ${formatCurrency(amountCents)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PausePlanDialog({ 
  open, 
  onOpenChange, 
  plan 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  plan: PaymentPlan;
}) {
  const { toast } = useToast();
  const [reason, setReason] = useState("");

  const remainingPauseDays = 90 - (plan.totalPauseDays || 0);
  const availablePauseOptions = [7, 14, 30, 60, 90].filter(d => d <= remainingPauseDays);
  const hasCustomRemainder = remainingPauseDays > 0 && remainingPauseDays < 7;
  const defaultPauseDays = hasCustomRemainder 
    ? remainingPauseDays.toString() 
    : availablePauseOptions.length > 0 
      ? (availablePauseOptions.includes(30) ? "30" : availablePauseOptions[0].toString())
      : "0";
  const [pauseDays, setPauseDays] = useState(defaultPauseDays);

  const pauseMutation = useMutation({
    mutationFn: (data: { reason: string; pauseDays: number }) => 
      apiRequest("POST", `/api/payment-plans/${plan.id}/pause`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentPlans });
      toast({ title: "Schedule Paused", description: "Your payment schedule has been paused." });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Pause Payment Schedule</DialogTitle>
          <DialogDescription>
            Request a temporary pause due to financial hardship
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm">
            <div className="flex items-center gap-2 font-medium text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="h-4 w-4" />
              Hardship Pause Policy
            </div>
            <p className="mt-2 text-yellow-700 dark:text-yellow-300">
              You can pause your payment schedule for up to 90 days total. 
              You have {remainingPauseDays} days remaining.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pauseDays">Pause Duration (days)</Label>
            {availablePauseOptions.length > 0 || hasCustomRemainder ? (
              <Select value={pauseDays} onValueChange={setPauseDays}>
                <SelectTrigger data-testid="select-pause-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hasCustomRemainder && (
                    <SelectItem value={remainingPauseDays.toString()}>
                      {remainingPauseDays} {remainingPauseDays === 1 ? "day" : "days"} (all remaining)
                    </SelectItem>
                  )}
                  {availablePauseOptions.map((days) => (
                    <SelectItem key={days} value={days.toString()}>
                      {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-destructive">
                You have used all 90 available pause days and cannot pause further.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Hardship</Label>
            <Textarea
              id="reason"
              placeholder="Please explain your financial situation..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[100px]"
              data-testid="input-pause-reason"
            />
            <p className="text-xs text-muted-foreground">Minimum 10 characters required</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={() => pauseMutation.mutate({ reason, pauseDays: parseInt(pauseDays) })}
            disabled={reason.length < 10 || pauseMutation.isPending || remainingPauseDays <= 0 || parseInt(pauseDays) > remainingPauseDays}
            data-testid="button-confirm-pause"
          >
            {pauseMutation.isPending ? "Pausing..." : "Pause Plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanDetailsDialog({ 
  open, 
  onOpenChange, 
  plan 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  plan: PaymentPlan;
}) {
  const { data: planDetails, isLoading } = useQuery<PaymentPlan & { payments: PaymentPlanPayment[] }>({
    queryKey: ["/api/payment-plans", plan.id],
    enabled: open,
  });

  const progress = ((plan.originalBalance - plan.remainingBalance) / plan.originalBalance) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {plan.hospitalName}
          </DialogTitle>
          <DialogDescription>
            {plan.serviceDescription || "Medical Services Payment Schedule"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {plan.status === "active" && !plan.isPaused && plan.nextPaymentDate && new Date(plan.nextPaymentDate) < new Date(new Date().toDateString()) && (
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  Overdue
                </Badge>
              )}
              <Badge className={getStatusColor(plan.status)}>
                {plan.isPaused ? "Paused" : plan.status}
              </Badge>
            </div>
            <span className="text-sm text-muted-foreground">
              Started {format(new Date(plan.startDate), "MMM d, yyyy")}
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span>Payment Progress</span>
              <span className="font-medium">{progress.toFixed(1)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-green-600 font-medium">{formatCurrency(plan.totalPaid)} paid</span>
              <span className="text-muted-foreground">{formatCurrency(plan.remainingBalance)} remaining</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Original Balance</p>
                <p className="text-xl font-bold">{formatCurrency(plan.originalBalance)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Payment Amount</p>
                <p className="text-xl font-bold">{formatCurrency(plan.paymentAmount)}/{plan.frequency}</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Payment History
            </h4>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {planDetails?.payments?.slice(0, 10).map((payment) => (
                  <div 
                    key={payment.id} 
                    className="flex items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getPaymentStatusIcon(payment.status)}
                      <div>
                        <p className="text-sm font-medium">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(payment.scheduledDate), "MMM d, yyyy")}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {payment.status}
                    </Badge>
                  </div>
                ))}
                {(!planDetails?.payments || planDetails.payments.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No payment history yet
                  </p>
                )}
              </div>
            )}
          </div>

          {plan.isPaused && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                <PauseCircle className="h-4 w-4" />
                Plan Paused
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Reason: {plan.pauseReason || "Not specified"}
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Resumes: {plan.pauseEndDate ? format(new Date(plan.pauseEndDate), "MMM d, yyyy") : "When resumed manually"}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
